import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { GoogleLogin } from '@react-oauth/google';
import { FacebookLoginButton } from '../components/FacebookLoginButton';
import { useAuth } from '../lib/auth';
import { authService } from '../lib/api-service';
import type { UserConsent } from '../lib/types';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../components/ui/alert-dialog';
import { User, Mail, Bell, Shield, ChevronRight, LogOut, CalendarCheck, Download, Trash2, FileText, Edit3, KeyRound, HandHelping } from 'lucide-react';
import { DateInput } from '../components/DateInput';

export default function Account() {
  const { user, loading, login, loginWithGoogle, loginWithFacebook, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const volunteerBioRedirectSlug = searchParams.get('volunteer_bio_required');

  // Login form state
  const [error, setError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [wasLoggedOut, setWasLoggedOut] = useState(!user);

  // Logged-in account state — always declared to satisfy Rules of Hooks
  const [consent, setConsent] = useState<UserConsent | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [volunteerBio, setVolunteerBio] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [volunteerRequestError, setVolunteerRequestError] = useState('');
  const [volunteerRequesting, setVolunteerRequesting] = useState(false);
  const [showVolunteerForm, setShowVolunteerForm] = useState(false);
  const [volFormDob, setVolFormDob] = useState('');
  const [volFormBio, setVolFormBio] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Staff/partner redirect after login
  useEffect(() => {
    if (wasLoggedOut && user) {
      const isStaffOrPartner = user.is_staff || (user.partner_ids && user.partner_ids.length > 0);
      if (isStaffOrPartner) {
        navigate('/staff', { replace: true });
        return;
      }
      setWasLoggedOut(false);
    }
  }, [user, wasLoggedOut, navigate]);

  // Load consent + sync profile fields when user changes
  useEffect(() => {
    if (!user) return;
    authService.getConsent().then(setConsent).catch(() => {});
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setPhone(user.profile?.phone || '');
    setDateOfBirth(user.profile?.date_of_birth || '');
    setVolunteerBio(user.profile?.volunteer_bio || '');
    setVolFormDob(user.profile?.date_of_birth || '');
    setVolFormBio(user.profile?.volunteer_bio || '');
    // Auto-open profile editor when redirected for missing bio
    if (volunteerBioRedirectSlug && !user.profile?.volunteer_bio?.trim()) {
      setEditingProfile(true);
    }
  }, [user, volunteerBioRedirectSlug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--vivid-purple)] border-t-transparent" />
      </div>
    );
  }

  // Not logged in — show login options
  if (!user) {
    return (
      <div className="bg-background" style={{ fontFamily: 'var(--font-primary)' }}>
        <div className="mx-auto max-w-sm px-4 py-16 text-center">
          <div
            className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold text-white"
            style={{ backgroundColor: '#3b0b86' }}
          >
            <User className="h-10 w-10" />
          </div>
          <h2
            className="mb-2 text-2xl font-bold"
            style={{ color: '#3b0b86', fontFamily: 'var(--font-primary)' }}
          >
            Contul Meu
          </h2>
          <p
            className="mb-8 text-sm text-muted-foreground"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            Conectează-te pentru a te înscrie la evenimente și a-ți gestiona contul
          </p>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {emailSent ? (
            <div className="text-center">
              <Mail className="mx-auto mb-4 h-10 w-10 text-[var(--vivid-purple)]" />
              <p className="mb-2 text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                Verifică-ți email-ul
              </p>
              <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Am trimis un link de conectare la {emailInput}
              </p>
            </div>
          ) : showPasswordForm ? (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSubmittingPassword(true);
              setError('');
              try {
                await login(username, password);
              } catch {
                setError('Nume de utilizator sau parolă incorectă.');
              } finally {
                setSubmittingPassword(false);
              }
            }}>
              <div className="mb-4">
                <input
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Utilizator"
                  autoFocus
                  className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                />
              </div>
              <div className="mb-4">
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Parolă"
                  className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                />
              </div>
              <button
                type="submit"
                disabled={submittingPassword}
                className="w-full rounded-lg bg-[var(--vivid-purple)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                {submittingPassword ? 'Se autentifică...' : 'Autentificare'}
              </button>
              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Înapoi
                </button>
                <Link
                  to="/staff/parola-uitata"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                >
                  Ai uitat parola?
                </Link>
              </div>
            </form>
          ) : showEmailForm ? (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSubmittingEmail(true);
              setError('');
              try {
                await authService.requestMagicLink(emailInput);
                setEmailSent(true);
              } catch {
                setError('A apărut o eroare. Încearcă din nou.');
              } finally {
                setSubmittingEmail(false);
              }
            }}>
              <div className="mb-4">
                <input
                  type="email"
                  required
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Adresa ta de email"
                  autoFocus
                  className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                />
              </div>
              <button
                type="submit"
                disabled={submittingEmail}
                className="w-full rounded-lg bg-[var(--vivid-purple)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                {submittingEmail ? 'Se trimite...' : 'Trimite link de conectare'}
              </button>
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground"
              >
                Înapoi
              </button>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={async (response) => {
                    if (response.credential) {
                      setError('');
                      try {
                        await loginWithGoogle(response.credential);
                      } catch {
                        setError('Autentificarea cu Google a eșuat.');
                      }
                    }
                  }}
                  onError={() => setError('Autentificarea cu Google a eșuat.')}
                  text="signin_with"
                  shape="rectangular"
                  width="308"
                />
              </div>
              <div className="mx-auto" style={{ maxWidth: 308 }}>
                <FacebookLoginButton
                  onSuccess={async (accessToken) => {
                    setError('');
                    try {
                      await loginWithFacebook(accessToken);
                    } catch {
                      setError('Autentificarea cu Facebook a eșuat.');
                    }
                  }}
                  onError={() => setError('Autentificarea cu Facebook a eșuat.')}
                />
              </div>
              <div className="mx-auto" style={{ maxWidth: 308 }}>
                <button
                  type="button"
                  onClick={() => setShowEmailForm(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-md border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-gray-50"
                  style={{ fontFamily: 'var(--font-primary)' }}
                >
                  <Mail className="h-5 w-5" />
                  Conectează-te cu email
                </button>
              </div>
              <div className="mx-auto" style={{ maxWidth: 308 }}>
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-md border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-gray-50"
                  style={{ fontFamily: 'var(--font-primary)' }}
                >
                  <KeyRound className="h-5 w-5" />
                  Conectează-te cu parolă
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    );
  }

  // Logged in — show account page
  const initials = user.first_name && user.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : user.email.substring(0, 2).toUpperCase();

  const displayName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.email;

  async function handleVolunteerRequest() {
    if (!volFormBio.trim()) {
      setVolunteerRequestError('Te rugăm să completezi bio-ul de voluntar.');
      return;
    }
    if (!volFormDob) {
      setVolunteerRequestError('Te rugăm să completezi data nașterii.');
      return;
    }
    setVolunteerRequesting(true);
    setVolunteerRequestError('');
    try {
      // Save profile with DOB + bio first
      await authService.updateMe({
        profile: { date_of_birth: volFormDob, volunteer_bio: volFormBio },
      });
      // Then request volunteer role
      await authService.requestVolunteerRole();
      await refreshUser();
      setShowVolunteerForm(false);
    } catch (err: any) {
      setVolunteerRequestError(err?.message || 'Eroare.');
    } finally {
      setVolunteerRequesting(false);
    }
  }

  async function handleSaveProfile() {
    setSavingProfile(true);
    setProfileMessage('');
    try {
      await authService.updateMe({
        first_name: firstName,
        last_name: lastName,
        profile: { phone: phone || '', date_of_birth: dateOfBirth || null, volunteer_bio: volunteerBio },
      });
      setEditingProfile(false);
      await refreshUser();
      // Redirect back to activity if we came from a volunteer bio flow
      if (volunteerBioRedirectSlug && volunteerBio.trim()) {
        navigate(`/eveniment/${volunteerBioRedirectSlug}`);
        return;
      }
      setProfileMessage('Datele au fost salvate.');
    } catch {
      setProfileMessage('Eroare la salvare. Verifică formatul numărului de telefon (ex: +40712345678).');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleToggleMarketing() {
    if (!consent) return;
    try {
      const updated = await authService.updateConsent({
        marketing_emails_accepted: !consent.marketing_emails_accepted,
      });
      setConsent(updated);
    } catch {
      // Ignore
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const data = await authService.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `datele-mele-ctro2026.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Ignore
    } finally {
      setExporting(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteConfirmation !== 'ȘTERGE') {
      setDeleteError('Tastează „ȘTERGE" pentru confirmare.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await authService.deleteAccount(deleteConfirmation);
      logout();
    } catch {
      setDeleteError('A apărut o eroare. Încearcă din nou.');
      setDeleting(false);
    }
  }

  return (
    <div className="bg-background" style={{ fontFamily: 'var(--font-primary)' }}>
      <div className="mx-auto max-w-4xl px-4 py-8">
        {/* Profile Header */}
        <div className="mb-8 rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div
              className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold text-white"
              style={{ backgroundColor: 'var(--vivid-purple)' }}
            >
              {initials}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold" style={{ color: 'var(--vivid-purple)', fontFamily: 'var(--font-primary)' }}>
                {displayName}
              </h2>
              <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                {user.email}
              </p>
            </div>
          </div>
        </div>

        {/* Zona Staff link — for staff/partner users */}
        {(user.is_staff || (user.partner_ids && user.partner_ids.length > 0)) && (
          <Link
            to="/staff"
            className="mb-4 flex items-center justify-between rounded-2xl p-5 shadow-sm transition-all hover:shadow-md"
            style={{ backgroundColor: 'var(--vivid-purple)' }}
          >
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-white" />
              <span className="font-semibold text-white" style={{ fontFamily: 'var(--font-primary)' }}>
                Zona Staff
              </span>
            </div>
            <ChevronRight className="h-5 w-5 text-white/70" />
          </Link>
        )}

        {/* My Events link */}
        <Link
          to="/cont/evenimente"
          className="mb-4 flex items-center justify-between rounded-2xl bg-white p-5 shadow-sm transition-all hover:shadow-md"
        >
          <div className="flex items-center gap-3">
            <CalendarCheck className="h-5 w-5" style={{ color: 'var(--vivid-purple)' }} />
            <span className="font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
              Evenimentele mele
            </span>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </Link>

        {/* Volunteer status / request */}
        {user.profile?.is_volunteer ? (
          <div className="mb-6 flex items-center gap-3 rounded-2xl bg-green-50 p-5 shadow-sm">
            <HandHelping className="h-5 w-5 text-green-600" />
            <span className="font-semibold text-green-700" style={{ fontFamily: 'var(--font-primary)' }}>
              Ești voluntar
            </span>
          </div>
        ) : user.profile?.volunteer_request_status === 'pending' ? (
          <div className="mb-6 flex items-center gap-3 rounded-2xl bg-amber-50 p-5 shadow-sm">
            <HandHelping className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-amber-700" style={{ fontFamily: 'var(--font-primary)' }}>
              Cererea ta de voluntar este în evaluare
            </span>
          </div>
        ) : user.profile?.volunteer_request_status === 'rejected' ? (
          <div className="mb-6 space-y-2">
            <div className="flex items-center gap-3 rounded-2xl bg-red-50 p-5 shadow-sm">
              <HandHelping className="h-5 w-5 text-red-500" />
              <div>
                <span className="font-semibold text-red-600" style={{ fontFamily: 'var(--font-primary)' }}>
                  Cererea de voluntar a fost respinsă
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowVolunteerForm(true)}
              className="w-full rounded-2xl border border-green-200 p-4 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50"
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              Solicită din nou
            </button>
          </div>
        ) : showVolunteerForm ? (
          <div className="mb-6 rounded-2xl border bg-white p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <HandHelping className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Vreau să fiu voluntar</h3>
            </div>
            {volunteerRequestError && (
              <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{volunteerRequestError}</div>
            )}
            {!user.profile?.date_of_birth && (
              <div>
                <label className="mb-1 block text-sm font-medium">Data nașterii *</label>
                <DateInput
                  value={volFormDob}
                  onChange={setVolFormDob}
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                />
                <p className="mt-1 text-xs text-muted-foreground">Trebuie să ai cel puțin 15 ani.</p>
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium">Despre mine ca voluntar *</label>
              <textarea
                value={volFormBio}
                onChange={(e) => setVolFormBio(e.target.value)}
                rows={3}
                placeholder="Scrie câteva cuvinte despre tine, experiența ta și de ce vrei să fii voluntar..."
                className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowVolunteerForm(false); setVolunteerRequestError(''); }}
                className="rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-gray-50"
              >
                Anulează
              </button>
              <button
                onClick={handleVolunteerRequest}
                disabled={volunteerRequesting}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              >
                <HandHelping className="h-4 w-4" />
                {volunteerRequesting ? 'Se trimite...' : 'Trimite cererea'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowVolunteerForm(true)}
            className="mb-6 flex w-full items-center justify-between rounded-2xl bg-white p-5 shadow-sm transition-all hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <HandHelping className="h-5 w-5 text-green-600" />
              <span className="font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                Vreau să fiu voluntar
              </span>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        )}

        {/* Profile - Edit personal data */}
        <div className="mb-6">
          <h3
            className="mb-3 px-4 text-sm font-semibold uppercase tracking-wide"
            style={{ color: '#717182', fontFamily: 'var(--font-secondary)' }}
          >
            Date Personale
          </h3>
          {volunteerBioRedirectSlug && editingProfile && (
            <div className="mb-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <HandHelping className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-800" style={{ fontFamily: 'var(--font-primary)' }}>
                  Completează bio-ul de voluntar
                </p>
                <p className="mt-0.5 text-xs text-amber-700" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Pentru a aplica ca voluntar la o activitate, trebuie să completezi câteva cuvinte despre tine în secțiunea „Despre mine ca voluntar". După salvare, vei fi redirecționat înapoi la activitate.
                </p>
              </div>
            </div>
          )}
          <div className="overflow-hidden rounded-2xl bg-white p-6 shadow-sm">
            {editingProfile ? (
              <div className="space-y-4" style={{ fontFamily: 'var(--font-secondary)' }}>
                <div>
                  <label className="mb-1 block text-sm font-medium">Prenume</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Nume</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Telefon</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+40..."
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Data nașterii</label>
                  <DateInput
                    value={dateOfBirth}
                    onChange={setDateOfBirth}
                    className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  />
                </div>
                {user.profile?.is_volunteer && (
                  <div>
                    <label className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                      <HandHelping className="h-4 w-4 text-green-600" />
                      Despre mine ca voluntar
                    </label>
                    <textarea
                      value={volunteerBio}
                      onChange={(e) => setVolunteerBio(e.target.value)}
                      rows={3}
                      placeholder="Scrie câteva cuvinte despre tine, experiența ta și de ce vrei să fii voluntar..."
                      className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Acest text este vizibil organizatorilor când aplici ca voluntar.</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="rounded-lg bg-[var(--vivid-purple)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-50"
                  >
                    {savingProfile ? 'Se salvează...' : 'Salvează'}
                  </button>
                  <button
                    onClick={() => setEditingProfile(false)}
                    className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
                  >
                    Anulează
                  </button>
                </div>
                {profileMessage && (
                  <p className="text-sm text-muted-foreground">{profileMessage}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2" style={{ fontFamily: 'var(--font-secondary)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Prenume</p>
                    <p className="font-medium">{user.first_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Nume</p>
                    <p className="font-medium">{user.last_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Telefon</p>
                    <p className="font-medium">{user.profile?.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Data nașterii</p>
                    <p className="font-medium">{user.profile?.date_of_birth || '—'}</p>
                  </div>
                  <button
                    onClick={() => setEditingProfile(true)}
                    className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-gray-50"
                  >
                    <Edit3 className="h-4 w-4" />
                    Editează
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notifications / Marketing consent */}
        <div className="mb-6">
          <h3
            className="mb-3 px-4 text-sm font-semibold uppercase tracking-wide"
            style={{ color: '#717182', fontFamily: 'var(--font-secondary)' }}
          >
            Notificări
          </h3>
          <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5" style={{ color: 'var(--vivid-purple)' }} />
                <span className="font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Emailuri despre activități și evenimente
                </span>
              </div>
              <button
                onClick={handleToggleMarketing}
                className={`relative h-6 w-11 rounded-full transition-colors ${
                  consent?.marketing_emails_accepted ? 'bg-[var(--vivid-purple)]' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                    consent?.marketing_emails_accepted ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Privacy / GDPR */}
        <div className="mb-6">
          <h3
            className="mb-3 px-4 text-sm font-semibold uppercase tracking-wide"
            style={{ color: '#717182', fontFamily: 'var(--font-secondary)' }}
          >
            Confidențialitate
          </h3>
          <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
            <Link
              to="/confidentialitate"
              className="flex w-full items-center justify-between border-b px-6 py-4 transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5" style={{ color: 'var(--vivid-purple)' }} />
                <span className="font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Politica de Confidențialitate
                </span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
            <Link
              to="/termeni"
              className="flex w-full items-center justify-between border-b px-6 py-4 transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5" style={{ color: 'var(--vivid-purple)' }} />
                <span className="font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Termeni și Condiții
                </span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex w-full items-center justify-between border-b px-6 py-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <Download className="h-5 w-5" style={{ color: 'var(--vivid-purple)' }} />
                <span className="font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
                  {exporting ? 'Se descarcă...' : 'Descarcă datele mele'}
                </span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="flex w-full items-center justify-between px-6 py-4 transition-colors hover:bg-red-50"
            >
              <div className="flex items-center gap-3">
                <Trash2 className="h-5 w-5 text-red-500" />
                <span className="font-medium text-red-600" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Șterge contul
                </span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Logout Button */}
        <div className="mt-8">
          <button
            onClick={() => logout()}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 font-semibold shadow-sm transition-all hover:shadow-md active:scale-98"
            style={{ color: 'var(--vivid-orange)', fontFamily: 'var(--font-primary)' }}
          >
            <LogOut className="h-5 w-5" />
            Deconectare
          </button>
        </div>

      </div>

      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergere cont</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Această acțiune este ireversibilă. Se vor întâmpla următoarele:</p>
                <ul className="list-inside list-disc space-y-1 text-sm">
                  <li>Toate datele personale vor fi anonimizate</li>
                  <li>Înscriierile la activitățile viitoare vor fi anulate</li>
                  <li>Nu vei mai putea accesa contul</li>
                </ul>
                <p className="font-medium">Tastează „ȘTERGE" pentru confirmare:</p>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="ȘTERGE"
                  className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                />
                {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmation(''); setDeleteError(''); }}>
              Anulează
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmation !== 'ȘTERGE'}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? 'Se șterge...' : 'Șterge contul definitiv'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
